import React, { useState, useEffect } from "react";
import { Checkbox } from "antd";
import { Spinner } from "react-bootstrap";
import { getPermissionModules } from "../../../service/api";
import {
  PERMISSION_MODULES as FALLBACK_MODULES,
  PERMISSION_ACTIONS as FALLBACK_ACTIONS,
  PERMISSION_CATEGORIES,
} from "../../../utils/rbac";

const PermissionMatrix = ({ value = [], onChange, disabled = false }) => {
  const [modules, setModules] = useState(FALLBACK_MODULES);
  const [actions, setActions] = useState(FALLBACK_ACTIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await getPermissionModules();
      if (res?.status && res?.data) {
        // Map backend modules to frontend format with categories
        const backendModules = res.data.modules || [];
        const mapped = backendModules.map((mod) => {
          // Find category from fallback list, default to "Other"
          const fallback = FALLBACK_MODULES.find((f) => f.key === mod.key);
          return {
            key: mod.key,
            label: mod.label || mod.key,
            category: fallback?.category || "Other",
          };
        });
        if (mapped.length > 0) {
          setModules(mapped);
        }
        if (res.data.actions && res.data.actions.length > 0) {
          setActions(res.data.actions);
        }
      }
    } catch (e) {
      // Use fallback modules on error
    }
    setLoading(false);
  };

  // Get all unique categories from current modules
  const categories = [
    ...new Set(modules.map((m) => m.category)),
  ];

  const getPermission = (moduleKey, action) => {
    const perm = value.find((p) => p.module === moduleKey);
    return perm ? perm[action] === true : false;
  };

  const handleChange = (moduleKey, action, checked) => {
    let updated = [...value];
    const idx = updated.findIndex((p) => p.module === moduleKey);

    if (idx >= 0) {
      updated[idx] = { ...updated[idx], [action]: checked };
      if (action === "view" && !checked) {
        updated[idx].create = false;
        updated[idx].update = false;
        updated[idx].delete = false;
      }
    } else {
      const newPerm = {
        module: moduleKey,
        view: false,
        create: false,
        update: false,
        delete: false,
        [action]: checked,
      };
      if (action !== "view" && checked) {
        newPerm.view = true;
      }
      updated.push(newPerm);
    }

    if (action !== "view" && checked && idx >= 0) {
      updated[idx].view = true;
    }

    onChange(updated);
  };

  const handleSelectAllModule = (moduleKey, checked) => {
    let updated = [...value];
    const idx = updated.findIndex((p) => p.module === moduleKey);

    const newPerm = {
      module: moduleKey,
      view: checked,
      create: checked,
      update: checked,
      delete: checked,
    };

    if (idx >= 0) {
      updated[idx] = newPerm;
    } else {
      updated.push(newPerm);
    }
    onChange(updated);
  };

  const handleSelectAllAction = (action, checked) => {
    let updated = modules.map((mod) => {
      const existing = value.find((p) => p.module === mod.key) || {
        module: mod.key,
        view: false,
        create: false,
        update: false,
        delete: false,
      };
      const newPerm = { ...existing, [action]: checked };
      if (action !== "view" && checked) {
        newPerm.view = true;
      }
      if (action === "view" && !checked) {
        newPerm.create = false;
        newPerm.update = false;
        newPerm.delete = false;
      }
      return newPerm;
    });
    onChange(updated);
  };

  const handleSelectAll = (checked) => {
    const updated = modules.map((mod) => ({
      module: mod.key,
      view: checked,
      create: checked,
      update: checked,
      delete: checked,
    }));
    onChange(updated);
  };

  const isAllSelected = () => {
    return modules.every((mod) =>
      actions.every((action) => getPermission(mod.key, action)),
    );
  };

  const isAllActionSelected = (action) => {
    return modules.every((mod) => getPermission(mod.key, action));
  };

  const isAllModuleSelected = (moduleKey) => {
    return actions.every((action) => getPermission(moduleKey, action));
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        className="table table-bordered"
        style={{ minWidth: "700px", marginBottom: 0 }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f8f9fa" }}>
            <th style={{ width: "40%" }}>
              <Checkbox
                checked={isAllSelected()}
                onChange={(e) => handleSelectAll(e.target.checked)}
                disabled={disabled}
              >
                <strong>Module</strong>
              </Checkbox>
            </th>
            {actions.map((action) => (
              <th key={action} style={{ textAlign: "center", width: "15%" }}>
                <Checkbox
                  checked={isAllActionSelected(action)}
                  onChange={(e) =>
                    handleSelectAllAction(action, e.target.checked)
                  }
                  disabled={disabled}
                >
                  <strong style={{ textTransform: "capitalize" }}>
                    {action}
                  </strong>
                </Checkbox>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((category) => {
            const categoryModules = modules.filter(
              (m) => m.category === category,
            );
            if (categoryModules.length === 0) return null;

            return (
              <React.Fragment key={category}>
                <tr>
                  <td
                    colSpan={actions.length + 1}
                    style={{
                      backgroundColor: "#e9ecef",
                      fontWeight: "bold",
                      fontSize: "13px",
                      padding: "8px 12px",
                    }}
                  >
                    {category}
                  </td>
                </tr>
                {categoryModules.map((mod) => (
                  <tr key={mod.key}>
                    <td style={{ paddingLeft: "24px" }}>
                      <Checkbox
                        checked={isAllModuleSelected(mod.key)}
                        onChange={(e) =>
                          handleSelectAllModule(mod.key, e.target.checked)
                        }
                        disabled={disabled}
                      >
                        {mod.label}
                      </Checkbox>
                    </td>
                    {actions.map((action) => (
                      <td key={action} style={{ textAlign: "center" }}>
                        <Checkbox
                          checked={getPermission(mod.key, action)}
                          onChange={(e) =>
                            handleChange(mod.key, action, e.target.checked)
                          }
                          disabled={disabled}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionMatrix;
